import { Badge } from '@amore/ui/components/badge'
import { Button } from '@amore/ui/components/button'
import { Card, CardHeader, CardTitle } from '@amore/ui/components/card'
import { useNavigate, useParams, useRouterState } from '@tanstack/react-router'
import { Clock3, Heart, Plus, Users } from 'lucide-react'
import { useRef } from 'react'
import { supplierStatusLabels, supplierStatusTone } from '@/lib/labels'
import { useCanEdit, useWedding } from '@/mock/store'
import type { CeremonyRole } from '@/mock/types'
import { type Column, DataTable } from '@/patterns/data-table'
import { DetailSheet } from '@/patterns/dialog'
import { EmptyState } from '@/patterns/empty-state'
import { ScreenSection } from '@/patterns/screen'
import brideIllustration from '../overview/assets/bride.svg'
import groomIllustration from '../overview/assets/groom.svg'
import { CeremonyEditAction } from './edit-action'
import { ceremonyPerson, ceremonySummary } from './model'
import { CeremonyOfficiantSheet } from './officiant-sheet'
import { CeremonyPersonIdentity } from './person-identity'
import { CeremonyRoleSheet } from './role-sheet'
import { CeremonyWitnessesSheet } from './witnesses-sheet'

export function CeremonyPeoplePage() {
  const data = useWedding()
  const canEdit = useCanEdit()
  const navigate = useNavigate()
  const addRoleButton = useRef<HTMLButtonElement>(null)
  const { roleId } = useParams({ strict: false })
  const role = data.ceremony.roles.find((item) => item.id === roleId)
  const closeRole = () => {
    void navigate({ to: '/ceremony/people' })
    requestAnimationFrame(() => {
      const target =
        (roleId ? document.getElementById(`ceremony-role-${roleId}`) : null) ??
        addRoleButton.current ??
        document.querySelector<HTMLAnchorElement>(
          'nav[aria-label="Stránky obřadu"] a[href="/ceremony/people"]',
        )
      target?.focus({ preventScroll: true })
    })
  }
  const openRole = (role: CeremonyRole) =>
    void navigate({ to: `/ceremony/people/roles/${role.id}` })
  const editingOfficiant = useRouterState({
    select: (state) => state.location.pathname === '/ceremony/people/officiant',
  })
  const editingWitnesses = useRouterState({
    select: (state) => state.location.pathname === '/ceremony/people/witnesses',
  })
  const closeDetail = (editLabel: string) => {
    void navigate({ to: '/ceremony/people' })
    requestAnimationFrame(() => {
      const target =
        document.querySelector<HTMLButtonElement>(`button[aria-label="${editLabel}"]`) ??
        document.querySelector<HTMLAnchorElement>(
          'nav[aria-label="Stránky obřadu"] a[href="/ceremony/people"]',
        )
      target?.focus({ preventScroll: true })
    })
  }
  const { officiant, brideWitness, groomWitness } = ceremonySummary(data)
  const witnesses = [
    {
      id: 'bride',
      illustration: brideIllustration,
      label: data.identity.brideName
        ? `Svědek · ${data.identity.brideName}`
        : 'Svědek prvního z páru',
      guest: brideWitness,
    },
    {
      id: 'groom',
      illustration: groomIllustration,
      label: data.identity.groomName
        ? `Svědek · ${data.identity.groomName}`
        : 'Svědek druhého z páru',
      guest: groomWitness,
    },
  ]
  const columns: Column<CeremonyRole>[] = [
    {
      key: 'role',
      header: 'Role',
      width: 'minmax(0,1fr)',
      primary: true,
      cell: (role) => <span className="break-words font-semibold">{role.title}</span>,
    },
    {
      key: 'person',
      header: 'Má na starosti',
      width: '19rem',
      interactive: true,
      cell: (role) => {
        const person = ceremonyPerson(role.person, data)
        return (
          <CeremonyPersonIdentity
            name={person?.name}
            to={person && 'to' in person ? person.to : undefined}
            empty="Zatím nepřiřazeno"
            compact
          />
        )
      },
    },
  ]

  return (
    <>
      <div className="grid gap-6 lg:grid-cols-2">
        <Card padding="none" className="overflow-hidden">
          <CardHeader appearance="strip">
            <div className="flex items-center gap-2.5">
              <Heart className="size-4 text-action" aria-hidden="true" />
              <CardTitle as="h2">Oddávající</CardTitle>
            </div>
            <CeremonyEditAction
              label="Upravit oddávajícího"
              onClick={() => void navigate({ to: '/ceremony/people/officiant' })}
            />
          </CardHeader>
          <div className="space-y-5 p-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <CeremonyPersonIdentity
                name={officiant?.name}
                to={officiant ? `/suppliers/${officiant.id}` : undefined}
              />
              {officiant ? (
                <Badge tone={supplierStatusTone(officiant.status)}>
                  {supplierStatusLabels[officiant.status]}
                </Badge>
              ) : null}
            </div>
            <div className="flex items-center justify-between gap-3 border-t border-border pt-4 text-sm">
              <span className="flex items-center gap-2 text-muted-foreground">
                <Clock3 className="size-4 shrink-0" aria-hidden="true" />
                Příjezd
              </span>
              <span
                className={
                  data.ceremony.officiantArrival
                    ? 'font-semibold tabular-nums'
                    : 'text-muted-foreground'
                }
              >
                {data.ceremony.officiantArrival || 'Zatím neurčený'}
              </span>
            </div>
          </div>
        </Card>
        <Card padding="none" className="overflow-hidden">
          <CardHeader appearance="strip">
            <div className="flex min-h-9 items-center gap-2.5">
              <Users className="size-4 text-action" aria-hidden="true" />
              <CardTitle as="h2">Svědci</CardTitle>
            </div>
            <CeremonyEditAction
              label="Upravit svědky"
              onClick={() => void navigate({ to: '/ceremony/people/witnesses' })}
            />
          </CardHeader>
          <div className="divide-y divide-border px-5">
            {witnesses.map(({ id, label, guest, illustration }) => (
              <div key={id} className="py-4">
                <CeremonyPersonIdentity
                  label={label}
                  illustration={illustration}
                  name={guest?.name}
                  to={guest ? `/guests/${guest.partyId}` : undefined}
                />
              </div>
            ))}
          </div>
        </Card>
      </div>
      <ScreenSection
        title="Další role"
        description="Kdo pomůže s prstýnky, usazením hostů nebo čímkoli dalším. Jen to, co potřebujete."
        action={
          canEdit ? (
            <Button
              ref={addRoleButton}
              onClick={() => void navigate({ to: '/ceremony/people/roles/new' })}
            >
              <Plus className="size-4" aria-hidden="true" />
              Přidat roli
            </Button>
          ) : undefined
        }
      >
        {data.ceremony.roles.length ? (
          <DataTable
            aria-label="Další role u obřadu"
            columns={columns}
            rows={data.ceremony.roles}
            getRowId={(role) => role.id}
            onRowClick={canEdit ? openRole : undefined}
            rowActions={
              canEdit
                ? (role) => (
                    <CeremonyEditAction
                      id={`ceremony-role-${role.id}`}
                      label={`Upravit roli ${role.title}`}
                      onClick={() => openRole(role)}
                    />
                  )
                : undefined
            }
          />
        ) : (
          <EmptyState
            icon={<Users />}
            title="Každý ví, s čím pomůže"
            description={
              canEdit
                ? 'Přidejte roli, až ji budete potřebovat, a vyberte člověka, na kterého se obrátíte.'
                : 'Žádné další role zatím nejsou přiřazené.'
            }
          />
        )}
      </ScreenSection>
      {editingOfficiant ? (
        <CeremonyOfficiantSheet onClose={() => closeDetail('Upravit oddávajícího')} />
      ) : null}
      {editingWitnesses ? (
        <CeremonyWitnessesSheet onClose={() => closeDetail('Upravit svědky')} />
      ) : null}
      {roleId === 'new' || role ? (
        <CeremonyRoleSheet key={roleId} role={role} onClose={closeRole} />
      ) : roleId ? (
        <DetailSheet
          open
          eyebrow="Lidé a role"
          title="Role už tu není"
          onOpenChange={(open) => {
            if (!open) closeRole()
          }}
        >
          <EmptyState
            icon={<Users />}
            title="Tuto roli se nepodařilo najít"
            description="Zavřete detail a vyberte jinou roli ze seznamu."
          />
        </DetailSheet>
      ) : null}
    </>
  )
}
